/**
 * P3-S2 릴레이 기계(SR-MODE-07 · FIELD-DEC-003) 렌더 도우미 — three 없이 시험하는 순수 함수.
 *  - {@link sectionFocus}: 구간 초점 카메라(칠판 자동 카메라 FS-12-01 · 학생 '내 구간 보기' FS-08-02)
 *  - {@link relaySectionTone} · {@link RELAY_SECTION_COLORS} · {@link planSectionTints}: 구간 상태 색(나감 상자 · 구간 벽 테두리).
 *    '다시 준비'(failed)는 **빨강 금지 — 중립색**(DR-01: 누구 구간이 실패했는지 드러내지 않는다 · 비교 없음).
 * 입력은 @picnic-field/modes relayLayout 결과(RelayLayout · RelaySection)를 구조적으로 받는다(렌더러는 modes 에 의존하지 않는다).
 */
import type { CourseBody, V3 } from "./courseMap";

/** 구간(RelaySection 에서 쓰는 필드만) — origin = 구간 틀 바닥 중심(x, z) · yaw = 0 | 180(도 · 흐름 +x | −x) */
export interface SectionLike {
  readonly origin: readonly [number, number];
  readonly yaw: number;
  readonly exitBox?: {
    readonly center: readonly [number, number, number];
    readonly half: readonly [number, number, number];
  };
}

/** relayLayout 결과(RelayLayout) — track.pitch = 구간 크기 + 틈(m) */
export interface SectionsLayoutLike {
  readonly sections: readonly SectionLike[];
  readonly track?: { readonly pitch: readonly [number, number, number] };
}

export interface SectionFocus {
  /** 바라볼 점(월드 m) — 구간 틀 가운데 · 바닥 조금 위 */
  readonly target: V3;
  /** 카메라가 target 에서 물러설 수평 거리(m) — 구간 틀이 화면에 다 들어오게(여백 포함) */
  readonly distance: number;
  /**
   * 바라보는 방향 yaw(라디안 · {@link yawOf} 규칙: +z = 0 · +x = +π/2) — 흐름이 화면 왼쪽 → 오른쪽이 되게(구간 yaw 0 → 0 · 180 → π).
   * 방향이 고정된 전시관 뷰(남쪽에서 북쪽을 본다)는 yaw 를 무시하고 `setViewpoint({x: target[0], z: target[2]}, {back: distance})`.
   */
  readonly yaw: number;
}

export interface SectionFocusOptions {
  /** 캔버스 너비/높이(기본 16/9) */
  aspect?: number;
  /** 세로 화각(도 · 기본 50 — 코스·전시관 뷰와 같다) */
  fovDeg?: number;
  /** 여백 배율(기본 1.2) */
  margin?: number;
  /** 가장 가까운 거리(m · 기본 1.2) */
  minDistance?: number;
  /** 구간 틀 크기 [x, z](m) — 없으면 layout.track.pitch · 그것도 없으면 나감 상자에서 어림(없으면 3 × 2.4) */
  size?: readonly [number, number];
}

const DEFAULT_SECTION: readonly [number, number] = [3, 2.4];

function footprint(
  src: readonly SectionLike[] | SectionsLayoutLike,
  s: SectionLike,
  o: SectionFocusOptions,
): readonly [number, number] {
  if (o.size) return o.size;
  const pitch = !Array.isArray(src) ? (src as SectionsLayoutLike).track?.pitch : undefined;
  if (pitch && pitch[0] > 0 && pitch[2] > 0) return [pitch[0], pitch[2]];
  if (s.exitBox) {
    // 나감 상자는 흐름 끝(+x 지역) 안쪽에 선다 — 틀 반 길이 ≈ 원점에서 상자 먼 가장자리까지
    const hx = Math.abs(s.exitBox.center[0] - s.origin[0]) + s.exitBox.half[0];
    const hz = Math.abs(s.exitBox.center[2] - s.origin[1]) + s.exitBox.half[2];
    return [Math.max(DEFAULT_SECTION[0], 2 * hx), Math.max(DEFAULT_SECTION[1], 2 * hz)];
  }
  return DEFAULT_SECTION;
}

/**
 * 구간 i 초점 — 구간 틀 가운데를 보고, 틀(가로 · 세로)이 화면에 다 들어오는 거리 · 흐름이 왼쪽 → 오른쪽인 방향.
 * 모르는 구간이면 null.
 */
export function sectionFocus(
  sectionsOrLayout: readonly SectionLike[] | SectionsLayoutLike,
  i: number,
  opts: SectionFocusOptions = {},
): SectionFocus | null {
  const list: readonly SectionLike[] = Array.isArray(sectionsOrLayout)
    ? sectionsOrLayout
    : (sectionsOrLayout as SectionsLayoutLike).sections;
  const s = Number.isInteger(i) ? list[i] : undefined;
  if (!s) return null;
  const [sx, sz] = footprint(sectionsOrLayout, s, opts);
  const aspect = opts.aspect && opts.aspect > 0 ? opts.aspect : 16 / 9;
  const tanV = Math.tan(((opts.fovDeg ?? 50) * Math.PI) / 180 / 2);
  const margin = opts.margin ?? 1.2;
  // 흐름 축(x)은 화면 가로 · 폭 축(z)은 화면 세로(비스듬히 내려다보므로 세로 여유를 그대로 쓴다)
  const needW = ((sx / 2) * margin) / (tanV * aspect);
  const needH = ((sz / 2) * margin) / tanV;
  const distance = Math.max(opts.minDistance ?? 1.2, needW, needH);
  const yaw = ((((s.yaw % 360) + 360) % 360) * Math.PI) / 180;
  return {
    target: [s.origin[0], 0.1, s.origin[1]],
    distance: Math.round(distance * 1000) / 1000,
    yaw: Math.abs(yaw - Math.PI) < 1e-9 ? Math.PI : yaw,
  };
}

// ── 구간 상태 색 ──────────────────────────────────────────────────────────

/** 표시 상태 5종(대기 · 진행 · 다시 준비 · 완료 · 건너뜀) */
export type RelaySectionTone = "waiting" | "active" | "retrying" | "done" | "skipped";

/**
 * 기본 색(sRGB) — Campus 토큰 계열. retrying 은 중립 청회색(빨강 · 주황 금지 — DR-01), skipped 는 중립 회색.
 * active = 포인트 노랑(지금 구간) · done = 초록.
 */
export const RELAY_SECTION_COLORS: Readonly<Record<RelaySectionTone, string>> = {
  waiting: "#D8E5DC",
  active: "#FFC845",
  retrying: "#9DB4CF",
  done: "#2E9E63",
  skipped: "#A9AEA2",
};

/**
 * RelayMachine 구간 상태(waiting · armed · running · done · failed · skipped) → 표시 상태. failed 는 '다시 준비'(retrying).
 * 모르는 값 · null 은 null(색을 바꾸지 않는다).
 */
export function relaySectionTone(status: string | null | undefined): RelaySectionTone | null {
  switch (status) {
    case "waiting":
      return "waiting";
    case "armed":
    case "running":
    case "active":
      return "active";
    case "failed":
    case "retrying":
      return "retrying";
    case "done":
      return "done";
    case "skipped":
      return "skipped";
    default:
      return null;
  }
}

/** 상태 → 색(색표를 일부 바꿀 수 있다 — 바꿔도 retrying 은 중립색이어야 한다: {@link isNeutralColor}) */
export function relaySectionColor(
  status: string | null | undefined,
  colors: Partial<Record<RelaySectionTone, string>> = {},
): string | null {
  const t = relaySectionTone(status);
  return t ? (colors[t] ?? RELAY_SECTION_COLORS[t]) : null;
}

/** 빨강 · 주황 계열이 아닌가(DR-01 검사 — 채도가 낮거나 색상이 빨강 ±40° 밖) */
export function isNeutralColor(hex: string): boolean {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return false;
  const n = parseInt(m[1]!, 16);
  const r = ((n >> 16) & 255) / 255;
  const g = ((n >> 8) & 255) / 255;
  const b = (n & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  const sat = max === 0 ? 0 : d / max;
  if (sat < 0.25) return true;
  let h: number;
  if (max === r) h = (((g - b) / d) % 6) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;
  h = (h + 360) % 360;
  return h > 40 && h < 320;
}

/** 몸체(조각 id 가 있으면 그것으로 구간을 안다 — relayLayout 조각 id `s{n}-exit` · `s{n}-wall-*`) */
export type SectionBody = CourseBody & { readonly piece?: string };

const PIECE = /^s(\d+)-(exit|wall-(?:in|out|l|r))$/;

/**
 * 구간 상태 → 몸체 색(나감 존 · 구간 벽 4개). statuses[i] = 구간 i 상태(RelayMachine 값 · null = 그대로).
 * 조각 id(BodyDesc.piece)가 없으면 relayLayout 환경 순서로 찾는다: k 번째 나감 존(zone · surface goal) = 구간 k ·
 * 벽(surface wall) 4개씩 = 구간 순서. 결과: 몸체 index → 색(없는 몸체는 표면 기본색).
 */
export function planSectionTints(
  bodies: readonly SectionBody[],
  statuses: readonly (string | null | undefined)[],
  colors: Partial<Record<RelaySectionTone, string>> = {},
): Map<number, string> {
  const out = new Map<number, string>();
  let zones = 0;
  let walls = 0;
  for (const b of bodies) {
    let section: number | null = null;
    const m = b.piece ? PIECE.exec(b.piece) : null;
    if (m) section = Number(m[1]) - 1;
    else if (!b.piece) {
      if (b.kind === "zone" && b.surface === "goal") section = zones++;
      else if (b.kind === "env" && b.surface === "wall") section = Math.floor(walls++ / 4);
    }
    if (section === null) continue;
    const c = relaySectionColor(statuses[section], colors);
    if (c) out.set(b.index, c);
  }
  return out;
}
